import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { Zap, Eye, EyeOff, Mail, Lock, Phone, User } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import PhoneVerifyDialog from "@/components/PhoneVerifyDialog";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";

export default function LoginPage() {
  const { user, login, googleLogin, loading, updateUser } = useAuth();
  const [tab, setTab] = useState("login"); // login | register | phone
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [phoneVerifyOpen, setPhoneVerifyOpen] = useState(false);
  const [phoneOtpStep, setPhoneOtpStep] = useState("phone"); // phone | otp
  const [phoneOtp, setPhoneOtp] = useState("");
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0f172a]">
        <div className="w-8 h-8 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) return <Navigate to={user.role === "ceo" ? "/dashboard" : user.role === "customer" ? "/dat-san" : "/"} replace />;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Đăng nhập thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (!phone || phone.replace(/\D/g, "").length < 9) {
      setError("Vui lòng nhập số điện thoại hợp lệ");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", { email, password, name, phone, role: "customer" });
      localStorage.setItem("token", data.token);
      window.location.href = "/dat-san";
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Đăng ký thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError("");
    try {
      const result = await googleLogin({ token: credentialResponse.credential });
      if (result.needs_phone) {
        setPhoneVerifyOpen(true);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Đăng nhập Google thất bại");
    }
  };

  const handleAppleLogin = () => {
    setError("Apple Sign In chưa được kích hoạt. Vui lòng liên hệ quản trị viên.");
  };

  const handlePhoneVerified = (updatedUser) => {
    updateUser(updatedUser);
  };

  const resetForm = (newTab) => { setTab(newTab); setError(""); setEmail(""); setPassword(""); setName(""); setPhone(""); };

  return (
    <div className="flex min-h-screen bg-[#0f172a]" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src="https://images.pexels.com/photos/35688546/pexels-photo-35688546.jpeg" alt="Pickleball court" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0f172a]/60" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-lime-500 flex items-center justify-center">
              <Zap className="w-7 h-7 text-[#0f172a]" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-white tracking-tight">KARINOX Arena</h1>
              <p className="text-slate-300 text-sm">Quản lý sân Pickleball chuyên nghiệp</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <div className="w-10 h-10 rounded-lg bg-lime-500 flex items-center justify-center">
              <Zap className="w-6 h-6 text-[#0f172a]" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-[#F8FAFC] tracking-tight">KARINOX Arena</h1>
          </div>

          {/* Tabs */}
          <div className="flex mb-6 bg-[#1e293b] rounded-lg p-1">
            <button onClick={() => resetForm("login")}
              className={`flex-1 py-2.5 rounded-md text-sm font-semibold transition-colors ${tab === "login" ? "bg-lime-500 text-[#0f172a]" : "text-slate-400 hover:text-slate-200"}`}
              data-testid="tab-login">
              Đăng nhập
            </button>
            <button onClick={() => { resetForm("phone"); setPhoneOtpStep("phone"); setPhoneOtp(""); }}
              className={`flex-1 py-2.5 rounded-md text-sm font-semibold transition-colors ${tab === "phone" ? "bg-amber-500 text-[#0f172a]" : "text-slate-400 hover:text-slate-200"}`}
              data-testid="tab-phone">
              SĐT + OTP
            </button>
            <button onClick={() => resetForm("register")}
              className={`flex-1 py-2.5 rounded-md text-sm font-semibold transition-colors ${tab === "register" ? "bg-teal-500 text-[#0f172a]" : "text-slate-400 hover:text-slate-200"}`}
              data-testid="tab-register">
              Đăng ký
            </button>
          </div>

          {tab === "login" ? (
            <>
              <div className="mb-6">
                <h2 className="font-heading text-2xl font-bold text-[#F8FAFC]">Đăng nhập</h2>
                <p className="text-slate-400 text-sm mt-1">Nhập thông tin để truy cập hệ thống</p>
              </div>
              <form onSubmit={handleLogin} className="space-y-4">
                {error && <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400" data-testid="login-error">{error}</div>}
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input type="email" placeholder="Nhập email" value={email} onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-[#1e293b] border-[#334155] text-slate-50 placeholder:text-slate-500 focus:border-lime-500 h-11"
                      required autoComplete="off" data-testid="login-email-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Mật khẩu</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input type={showPw ? "text" : "password"} placeholder="Nhập mật khẩu" value={password} onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 bg-[#1e293b] border-[#334155] text-slate-50 placeholder:text-slate-500 focus:border-lime-500 h-11"
                      required data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={submitting}
                  className="w-full bg-lime-500 text-[#0f172a] font-semibold hover:bg-lime-400 h-11 rounded-lg" data-testid="login-submit-btn">
                  {submitting ? "Đang xử lý..." : "Đăng nhập"}
                </Button>
                <div className="flex justify-center mt-2">
                  <button type="button"
                    onClick={() => setForgotPasswordOpen(true)}
                    className="text-sm text-cyan-400 hover:text-cyan-300 hover:underline transition-colors"
                    data-testid="forgot-password-link">
                    Quên mật khẩu?
                  </button>
                </div>
              </form>
            </>
          ) : tab === "phone" ? (
            <>
              <div className="mb-6">
                <h2 className="font-heading text-2xl font-bold text-[#F8FAFC]">Đăng nhập bằng SĐT</h2>
                <p className="text-slate-400 text-sm mt-1">Nhận mã OTP qua Zalo — dành cho admin, staff & khách hàng</p>
              </div>
              <div className="space-y-4">
                {error && <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400" data-testid="phone-login-error">{error}</div>}
                {phoneOtpStep === "phone" ? (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-slate-300 text-sm">Số điện thoại</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input type="tel" placeholder="0901234567" value={phone} onChange={(e) => { setPhone(e.target.value); setError(""); }}
                          className="pl-10 bg-[#1e293b] border-[#334155] text-slate-50 placeholder:text-slate-500 focus:border-amber-500 h-12 text-lg"
                          autoFocus data-testid="phone-login-input" />
                      </div>
                    </div>
                    <Button onClick={async () => {
                      if (!phone || phone.replace(/\D/g, "").length < 9) { setError("SĐT không hợp lệ"); return; }
                      setSubmitting(true); setError("");
                      try {
                        await api.post("/auth/phone-login-otp", { phone: phone.trim() });
                        setPhoneOtpStep("otp");
                      } catch (err) { setError(err.response?.data?.detail || "Lỗi gửi OTP"); }
                      finally { setSubmitting(false); }
                    }} disabled={submitting}
                      className="w-full bg-amber-500 hover:bg-amber-400 text-[#0f172a] font-semibold h-11" data-testid="phone-login-send-otp">
                      {submitting ? "Đang gửi..." : "Gửi mã OTP qua Zalo"}
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-slate-400">Nhập mã OTP nhận qua tin nhắn Zalo vào số <b className="text-amber-400">{phone}</b></p>
                    <div className="space-y-1.5">
                      <Label className="text-slate-300 text-sm">Mã OTP (6 số)</Label>
                      <Input type="text" maxLength={6} placeholder="000000" value={phoneOtp}
                        onChange={(e) => { setPhoneOtp(e.target.value.replace(/\D/g, "")); setError(""); }}
                        className="bg-[#1e293b] border-[#334155] text-slate-50 text-center text-2xl tracking-[0.3em] font-mono h-14 focus:border-amber-500"
                        autoFocus data-testid="phone-login-otp-input" />
                    </div>
                    <Button onClick={async () => {
                      if (phoneOtp.length !== 6) { setError("Nhập đủ 6 số"); return; }
                      setSubmitting(true); setError("");
                      try {
                        const { data } = await api.post("/auth/phone-login-verify", { phone: phone.trim(), otp: phoneOtp });
                        localStorage.setItem("token", data.token);
                        window.location.href = "/dat-san";
                      } catch (err) { setError(err.response?.data?.detail || "OTP không đúng"); }
                      finally { setSubmitting(false); }
                    }} disabled={submitting || phoneOtp.length !== 6}
                      className="w-full bg-amber-500 hover:bg-amber-400 text-[#0f172a] font-semibold h-11" data-testid="phone-login-verify-btn">
                      {submitting ? "Đang xác nhận..." : "Xác nhận & Đăng nhập"}
                    </Button>
                    <button type="button" onClick={() => { setPhoneOtpStep("phone"); setPhoneOtp(""); setError(""); }}
                      className="text-xs text-slate-500 hover:text-slate-300 w-full text-center">
                      Đổi số / Gửi lại OTP
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="font-heading text-2xl font-bold text-[#F8FAFC]">Đăng ký tài khoản</h2>
                <p className="text-slate-400 text-sm mt-1">Tạo tài khoản để đặt sân online</p>
              </div>
              <form onSubmit={handleRegister} className="space-y-4">
                {error && <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400" data-testid="register-error">{error}</div>}
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Họ tên *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input placeholder="Nguyễn Văn A" value={name} onChange={(e) => setName(e.target.value)}
                      className="pl-10 bg-[#1e293b] border-[#334155] text-slate-50 placeholder:text-slate-500 focus:border-teal-500 h-11"
                      required data-testid="register-name-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Số điện thoại *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input type="tel" placeholder="0901234567" value={phone} onChange={(e) => setPhone(e.target.value)}
                      className="pl-10 bg-[#1e293b] border-[#334155] text-slate-50 placeholder:text-slate-500 focus:border-teal-500 h-11"
                      required data-testid="register-phone-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input type="email" placeholder="email@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-[#1e293b] border-[#334155] text-slate-50 placeholder:text-slate-500 focus:border-teal-500 h-11"
                      required data-testid="register-email-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Mật khẩu *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input type={showPw ? "text" : "password"} placeholder="Tối thiểu 6 ký tự" value={password} onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 bg-[#1e293b] border-[#334155] text-slate-50 placeholder:text-slate-500 focus:border-teal-500 h-11"
                      required minLength={6} data-testid="register-password-input" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={submitting}
                  className="w-full bg-teal-500 text-[#0f172a] font-semibold hover:bg-teal-400 h-11 rounded-lg" data-testid="register-submit-btn">
                  {submitting ? "Đang xử lý..." : "Đăng ký & Đặt sân"}
                </Button>
              </form>
            </>
          )}

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#334155]" /></div>
            <div className="relative flex justify-center text-xs"><span className="px-3 bg-[#0f172a] text-slate-500">hoặc</span></div>
          </div>
          <div className="flex flex-col gap-3" data-testid="social-login-container">
            <div className="flex justify-center">
              <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setError("Đăng nhập Google thất bại")} theme="filled_black" size="large" width="400" text="signin_with" locale="vi" />
            </div>
            <button onClick={handleAppleLogin}
              className="w-full h-11 bg-white text-black font-semibold rounded-lg flex items-center justify-center gap-2.5 hover:bg-gray-100 transition-colors"
              data-testid="apple-login-btn">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
              Đăng nhập với Apple
            </button>
          </div>

          <PhoneVerifyDialog open={phoneVerifyOpen} onOpenChange={setPhoneVerifyOpen} onVerified={handlePhoneVerified} />
          <ForgotPasswordDialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen} />
        </div>
      </div>
    </div>
  );
}
